import React from "react";
import { Link } from "react-router-dom";

const AutomationPage: React.FC = () => {
  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-white">
        Evolution of Google Ads Automation
      </h1>

      <div className="mb-8 flex justify-center">
        <img
          src="/images/automation.jpeg"
          alt="Evolution of Google Ads Automation"
          className="rounded-lg shadow-xl max-w-full md:max-w-3xl"
        />
      </div>

      <div className="mb-10 px-4 md:px-0">
        <h2 className="text-2xl font-semibold text-center mb-4 text-gray-800 dark:text-white">
          From Ads to AI
        </h2>
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          The journey of managing Google Ads has transformed dramatically. From
          tedious manual adjustments to sophisticated AI-driven strategies,
          automation has continuously reshaped how advertisers approach campaign
          management. This page outlines the typical stages of this evolution,
          reflecting a path many of us have traveled or are currently
          navigating. Understanding these levels can help pinpoint where you are
          and what the next steps in leveraging automation might look like for
          your business.
        </p>
      </div>

      <div className="space-y-10 text-gray-700 dark:text-gray-300">
        <div>
          <h2 className="text-2xl font-semibold mb-3 text-indigo-600 dark:text-indigo-400">
            Level 0: Button Clicker - Manual Management
          </h2>
          <p className="text-lg leading-relaxed">
            Remember spending hours manually adjusting bids? This is where we
            all started – as Button Clickers. Manual ad management with basic
            clicks.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-sky-600 dark:text-sky-400">
            Level 1: Rule Designer - Rule-Based Automation
          </h2>
          <p className="text-lg leading-relaxed">
            Simple "if this, then that" rules. As Rule Designers, we established
            basic guardrails, creating rules for ad automation.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-cyan-600 dark:text-cyan-400">
            Level 2: Target Setter - In-Platform AI
          </h2>
          <p className="text-lg leading-relaxed">
            Google's Smart Bidding changed everything. Target Setters define
            specific goals for ad campaigns and focus on other tasks (creative,
            audiences, landers) while Google's algorithms handle bid
            optimization.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-lime-600 dark:text-lime-400">
            Level 3: Script Designer - Scripts & External Data
          </h2>
          <p className="text-lg leading-relaxed">
            Script Designers create powerful automation workflows without
            coding. They use off-the-shelf scripts or leverage AI to adjust
            existing scripts for custom needs. These solutions can extract data
            and connect with external systems (Sheets FTW!), expanding what's
            possible beyond the Ads UI by writing scripts to improve ad
            performance.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-yellow-500 dark:text-yellow-400">
            Level 4: Insight Storyteller - AI-Assisted Insights
          </h2>
          <p className="text-lg leading-relaxed">
            Via a script or app, LLMs now analyze your data to generate 80%
            good-enough insights. Insight Storytellers take these AI
            observations and craft them into meaningful narratives from AI
            insights, adding the crucial human context and business
            understanding that AI often lacks.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-orange-500 dark:text-orange-400">
            Level 5: Agent Conductor - AI-Driven Actions
          </h2>
          <p className="text-lg leading-relaxed">
            Agent Conductors orchestrate LLMs to not just analyze but take
            action within pre-approved boundaries. They design feedback loops
            and testing frameworks to ensure AI-driven changes deliver real
            performance improvements by orchestrating AI agents for ad
            management.
          </p>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-red-600 dark:text-red-400">
            Level 6: Business Architect - Autonomous AI Agents
          </h2>
          <p className="text-lg leading-relaxed">
            Business Architects design comprehensive systems where AI agents
            autonomously pursue business objectives. Far beyond simply setting
            bid targets, they create prompt evaluation frameworks and consider
            ethical boundaries that let AI handle execution while humans focus
            on building businesses. This involves designing comprehensive Agents
            for the entire business.
          </p>
        </div>
      </div>

      <div className="mt-12 mb-6 px-4 md:px-0 text-center">
        <h3 className="text-xl font-semibold mb-3 text-gray-800 dark:text-white">
          Ready to Dive Deeper?
        </h3>
        <div className="w-full md:w-1/2 mx-auto">
          <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-4">
            Interested in joining a community of like-minded professionals who
            are also navigating the shift from traditional ads management to
            AI-powered strategies? Share insights, learn from others, and stay
            ahead of the curve.
          </p>
        </div>
        <Link
          to="/ai"
          className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-colors duration-300 ease-in-out"
        >
          What do I get as a Member?
        </Link>
      </div>
    </div>
  );
};

export default AutomationPage;
